import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnInit, OnDestroy, HostListener, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { LanguageService } from '@mzic/mzic-services';
import { MzicButtonComponent, MzicBoxComponent } from '@mzic/mzic-arpeggio-ui';
import { HeaderComponent } from '../../components/header/header.component';
import { QRDownloadModalComponent, QRModalConfig } from '../../components/qr-download-modal/qr-download-modal.component';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
  selector: 'mzic-desktop-unavailable',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    TranslocoModule,
    RouterModule,
    MzicButtonComponent,
    MzicBoxComponent,
    QRDownloadModalComponent
  ],
  templateUrl: './desktop-unavailable.component.html',
  styleUrl: './desktop-unavailable.component.scss',
  providers: [
    provideTranslocoScope({
      scope: 'desktop-unavailable',
      alias: 'lang',
    }),
  ],
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px)' }),
        animate('0.8s ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('bounceIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.3)' }),
        animate('0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)', 
          style({ opacity: 1, transform: 'scale(1)' }))
      ])
    ]),
    trigger('slideInLeft', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-50px)' }),
        animate('0.6s 0.2s cubic-bezier(0.4, 0, 0.2, 1)', 
          style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('slideInRight', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(50px)' }),
        animate('0.6s 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
          style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('deviceFloat', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('0.8s 0.6s cubic-bezier(0.4, 0, 0.2, 1)', 
          style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('pulseGlow', [
      state('pulse', style({ transform: 'scale(1)' })),
      transition('* => pulse', [
        animate('2s ease-in-out', style({ transform: 'scale(1.05)' })),
        animate('2s ease-in-out', style({ transform: 'scale(1)' }))
      ])
    ])
  ]
})
export class DesktopUnavailableComponent implements OnInit, OnDestroy, AfterViewInit {
  languageService = inject(LanguageService);
  activatedRoute = inject(ActivatedRoute);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  
  mouseX = 0;
  mouseY = 0;
  private scrollHandler?: () => void;
  
  floatingElements = [
    { icon: '📱', delay: 0, x: 10, y: 20 },
    { icon: '💻', delay: 1000, x: 80, y: 15, crossed: true },
    { icon: '📲', delay: 2000, x: 90, y: 70 },
    { icon: '⚡', delay: 1500, x: 15, y: 80 },
    { icon: '🚀', delay: 2500, x: 70, y: 85 },
    { icon: '📱', delay: 500, x: 85, y: 25 }
  ];

  appStoreUrl = 'https://mzic.app.link/app';
  playStoreUrl = 'https://mzic.app.link/app';
  
  // QR Modal properties
  showQRModal = false;
  activeStore: 'appstore' | 'playstore' | null = null;
  
  // QR Modal configuration
  qrModalConfig: QRModalConfig = {
    appStoreUrl: this.appStoreUrl,
    playStoreUrl: this.playStoreUrl,
    qrCodeUrls: {
      appstore: 'https://cdn.dev.mzic.io/static/image/download-app.png',
      playstore: 'https://cdn.dev.mzic.io/static/image/download-app.png'
    }
  };

  constructor() {
    effect(() => {
      const lang = this.languageService.languageState();
      if (lang) {
        this.translocoService.setActiveLang(lang);
      }
    });
  }

  ngOnInit() {
    this.setLanguageFromRoute();
  }

  ngAfterViewInit() {
    this.initializeParallax();
    this.animateFloatingElements();
  }

  ngOnDestroy() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
    this.updateGradients();
  }

  setLanguageFromRoute() {
    this.activatedRoute.params.subscribe((params) => {
      const lang = params['lang'];
      if (lang) {
        this.translocoService.setActiveLang(lang);
        this.languageService.languageState.set(lang);
      }
    });
  }

  navigateToHome() {
    const lang = this.languageService.languageState() || 'en';
    this.router.navigate([`/${lang}/home`]);
  }

  navigateToAssets() {
    const lang = this.languageService.languageState() || 'en';
    this.router.navigate([`/${lang}/assets`]);  
  }

  goBack() {
    window.history.back();
  }

  initializeParallax() {
    const parallaxElements = document.querySelectorAll('.parallax-element');
    
    this.scrollHandler = () => {
      const scrolled = window.pageYOffset;
      const rate = scrolled * -0.5;
      
      parallaxElements.forEach(element => {
        (element as HTMLElement).style.transform = `translateY(${rate}px)`;
      });
    };
    
    window.addEventListener('scroll', this.scrollHandler);
  }

  updateGradients() {
    const mouseGradient = document.querySelector('.mouse-gradient') as HTMLElement;
    if (mouseGradient) {
      const rect = mouseGradient.getBoundingClientRect();
      const x = ((this.mouseX - rect.left) / rect.width) * 100;
      const y = ((this.mouseY - rect.top) / rect.height) * 100;
      
      mouseGradient.style.background = `
        radial-gradient(600px circle at ${x}% ${y}%, 
          rgba(150, 85, 255, 0.15) 0%, 
          rgba(150, 85, 255, 0.05) 30%, 
          transparent 50%)
      `;
    }
  }

  animateFloatingElements() {
    this.floatingElements.forEach((element, index) => {
      setTimeout(() => {
        const elementDOM = document.querySelector(`.floating-tech-${index}`) as HTMLElement;
        if (elementDOM) {
          elementDOM.style.animation = `float-tech 4s ease-in-out infinite`;
          elementDOM.style.animationDelay = `${element.delay}ms`;
        }
      }, element.delay);
    });
  }

  openAppStore() {
    window.open(this.appStoreUrl, '_blank', 'noopener,noreferrer');
  }

  openPlayStore() {
    window.open(this.playStoreUrl, '_blank', 'noopener,noreferrer');
  }
  
  // QR Modal methods
  openQRModal(store: 'appstore' | 'playstore') {
    this.activeStore = store;
    this.showQRModal = true;
  }
  
  onCloseQRModal() {
    this.showQRModal = false;
    this.activeStore = null;
  }
  
  onDirectLinkClick(store: 'appstore' | 'playstore') {
    const url = store === 'appstore' ? this.appStoreUrl : this.playStoreUrl;
    window.open(url, '_blank', 'noopener,noreferrer');
    this.onCloseQRModal();
  }
} 